"use client";

import type React from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ChakraProvider,
  Box,
  Spinner,
  Center,
  ColorModeScript,
  Text,
  VStack,
} from "@chakra-ui/react";
import { supabase } from "./supabaseClient";
import theme from "./theme";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import SignIn from "./components/SignIn";
import MfaGate from "./components/MfaGate";
import Dashboard from "./components/Dashboard";
import AgentsPage from "./components/AgentsPage";
import RecordingPlayer from "./components/RecordingPlayer";
import VoiceAgents from "./components/VoiceAgents";
import SMSAgents from "./components/SMSAgents";
import EmailAgents from "./components/EmailAgents";
import WhatsAppAgents from "./components/WhatsAppAgents";
import UsagePage from "./components/UsagePage";
import BillingPage from "./components/BillingPage";
import Layout from "./components/Layout";
import InviteAccept from "./components/InviteAccept";
import PasswordReset from "./components/PasswordReset";
import PasswordResetOTP from "./components/PasswordResetOTP";
import SetNewPassword from "./components/SetNewPassword";
import AppWelcome from "./components/AppWelcome";
import InviteVerification from "./components/InviteVerification";
import AgentRouter from "./components/voice-agents/AgentRouter";
import SMSAgentRouter from "./components/sms-agents/SMSAgentRouter";
import WhatsAppAgentRouter from "./components/whatsapp-agents/WhatsAppAgentRouter";
import EmailAgentRouter from "./components/email-agents/EmailAgentRouter";
import CampaignsPage from "./components/CampaignsPage";
import CampaignConfig from "./components/CampaignConfig";
import PublicHomepage from "./components/PublicHomepage";
import TeamManagement from "./components/TeamManagement";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="primary.500" />
          <Text>Loading...</Text>
        </VStack>
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <MfaGate>
      <Layout>{children}</Layout>
    </MfaGate>
  );
};

/** Route that requires account_admin or super_admin. Redirects regular users to /dashboard. */
const AdminRoute: React.FC<{ children: React.ReactNode; check?: 'billing' | 'users' }> = ({ children, check = 'billing' }) => {
  const { user, loading, profileLoading, canViewBilling, canManageUsers } = useAuth();

  if (loading || profileLoading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="primary.500" />
          <Text>Loading...</Text>
        </VStack>
      </Center>
    );
  }

  if (!user) return <Navigate to="/signin" replace />;

  const allowed = check === 'users' ? canManageUsers : canViewBilling;
  if (!allowed) {
    // TODO: Surface a toast to the user (e.g. pass state={{ accessDenied: true }} and
    // handle it in Dashboard/Layout) — for now we log a warning and redirect silently.
    console.warn('[AdminRoute] Access denied: user lacks required role for', check, '— redirecting to /dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MfaGate>
      <Layout>{children}</Layout>
    </MfaGate>
  );
};

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="primary.500" />
          <Text>Loading application...</Text>
        </VStack>
      </Center>
    );
  }

  const isMainApp = process.env.REACT_APP_IS_MAIN_APP === "true";

  return (
    <Routes>
      <Route
        path="/signin"
        element={user ? <Navigate to="/dashboard" replace /> : <SignIn />}
      />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/password-reset-otp" element={<PasswordResetOTP />} />
      <Route path="/set-new-password" element={<SetNewPassword />} />
      <Route path="/invite" element={<InviteAccept />} />
      <Route path="/invite-verify" element={<InviteVerification />} />
      <Route path="/app" element={<AppWelcome />} />

      {/* Public, shareable call recording player (no login required) */}
      <Route path="/recordings/:callId/player" element={<RecordingPlayer />} />

      {isMainApp && (
        <>
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </>
      )}

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-agents"
        element={
          <ProtectedRoute>
            <AgentsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/agents" element={<Navigate to="/all-agents" replace />} />
      <Route
        path="/voice-agents"
        element={
          <ProtectedRoute>
            <VoiceAgents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-agents"
        element={
          <ProtectedRoute>
            <SMSAgents />
          </ProtectedRoute>
        }
      />
      {/* EMAIL ROUTES → ONLY WHEN isMainApp === true */}
      {isMainApp && (
        <Route
          path="/email-agents"
          element={
            <ProtectedRoute>
              <EmailAgents />
            </ProtectedRoute>
          }
        />
      )}
      <Route
        path="/whatsapp-agents"
        element={
          <ProtectedRoute>
            <WhatsAppAgents />
          </ProtectedRoute>
        }
      />
      {/* Individual agent detail routes */}
      <Route
        path="/voice-agents/:agentId"
        element={
          <ProtectedRoute>
            <AgentRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-agents/:agentId"
        element={
          <ProtectedRoute>
            <SMSAgentRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp-agents/:agentId"
        element={
          <ProtectedRoute>
            <WhatsAppAgentRouter />
          </ProtectedRoute>
        }
      />
      {isMainApp && (
        <Route
          path="/email-agents/:agentId"
          element={
            <ProtectedRoute>
              <EmailAgentRouter />
            </ProtectedRoute>
          }
        />
      )}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            <CampaignConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage"
        element={
          <ProtectedRoute>
            <UsagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <AdminRoute>
            <BillingPage />
          </AdminRoute>
        }
      />
      <Route
        path="/team"
        element={
          <AdminRoute check="users">
            <TeamManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/"
        element={(() => {
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get("token");
          const type = urlParams.get("type");

          if (token && type === "invite") {
            return (
              <Navigate
                to={`/invite-verify${window.location.search}`}
                replace
              />
            );
          }

          return user ? (
            <Navigate to="/dashboard" replace />
          ) : isMainApp ? (
            <PublicHomepage />
          ) : (
            <Navigate to="/signin" replace />
          );
        })()}
      />
    </Routes>
  );
};

function App() {
  const [appLoading, setAppLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const addDebugInfo = (info: string) => {
    console.log(`[DEBUG] ${info}`);
    setDebugInfo((prev) => [...prev, `${new Date().toISOString()}: ${info}`]);
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        addDebugInfo("App initialization started");

        // Check environment variables
        addDebugInfo(
          `Supabase URL: ${
            process.env.REACT_APP_SUPABASE_URL ? "Set" : "NOT SET"
          }`
        );
        addDebugInfo(
          `Supabase Anon Key: ${
            process.env.REACT_APP_SUPABASE_ANON_KEY ? "Set" : "NOT SET"
          }`
        );
        addDebugInfo(
          `Server URL: ${process.env.REACT_APP_SERVER_URL || "NOT SET"}`
        );

        // Test Supabase connection
        addDebugInfo("Testing Supabase connection...");
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          addDebugInfo(`Supabase error: ${error.message}`);
          // Don't throw error, continue with mock functionality
        } else {
          addDebugInfo("Supabase connection successful");
          addDebugInfo(`Session exists: ${data.session ? "Yes" : "No"}`);
        }

        // Test if we can reach our server (optional)
        if (process.env.REACT_APP_SERVER_URL) {
          try {
            addDebugInfo("Testing server connection...");
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_URL}/health`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (response.ok) {
              addDebugInfo("Server connection successful");
            } else {
              addDebugInfo(`Server responded with status: ${response.status}`);
            }
          } catch (serverError: any) {
            addDebugInfo(`Server connection failed: ${serverError.message}`);
          }
        }

        addDebugInfo("App initialization completed");
        setAppLoading(false);
      } catch (error: any) {
        addDebugInfo(`App initialization failed: ${error.message}`);
        console.error("App initialization error:", error);
        // Still set loading to false to show the error state
        setAppLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (appLoading) {
    return (
      <ChakraProvider theme={theme}>
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" />
            <Text>Loading application...</Text>
            {process.env.NODE_ENV === "development" && (
              <Box maxW="md" p={4} bg="gray.100" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" mb={2}>
                  Debug Info:
                </Text>
                {debugInfo.map((info, index) => (
                  <Text key={index} fontSize="xs" color="gray.600">
                    {info}
                  </Text>
                ))}
              </Box>
            )}
          </VStack>
        </Center>
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <AuthProvider>
        <Router>
          <Box minH="100vh" w="100%">
            <AppRoutes />
          </Box>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
